import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import service from '../service';
import type { TicketedEvent } from '../types/ticketedEvents';
import type { UpdateTicketedEventPayload } from '../types/api';

export interface UpdateTicketedEventVariables {
  eventId: string;
  payload: UpdateTicketedEventPayload;
}

export type UpdateTicketedEventResult = TicketedEvent;

/**
 * useUpdateTicketedEvent
 *
 * Mutation hook for updating a ticketed event.
 *
 * @example
 * const { mutateAsync: updateTicketedEvent, isPending, error } = useUpdateTicketedEvent();
 *
 * await updateTicketedEvent({
 *   eventId: '123',
 *   payload: {
 *     featuredImageId: 'asset-id-1',
 *   },
 * });
 */
export function useUpdateTicketedEvent(
  options?: UseMutationOptions<
    UpdateTicketedEventResult,
    Error,
    UpdateTicketedEventVariables
  >,
): UseMutationResult<
  UpdateTicketedEventResult,
  Error,
  UpdateTicketedEventVariables
> {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation<
    UpdateTicketedEventResult,
    Error,
    UpdateTicketedEventVariables
  >({
    mutationKey: ['ticketedEvent', 'update'],
    mutationFn: async ({ eventId, payload }) => {
      return service.updateTicketedEvent(eventId, payload);
    },
    retry: (failureCount, error: any) => {
      const status =
        error?.status ??
        error?.response?.status ??
        error?.cause?.status ??
        null;
      const isServerOrNetwork = status == null || status >= 500;
      return failureCount < 2 && isServerOrNetwork;
    },
    ...restOptions,
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate ticketed event queries to refresh data including featured image
      queryClient.invalidateQueries({
        queryKey: ['ticketedEvent', variables.eventId],
      });
      queryClient.invalidateQueries({ queryKey: ['ticketed-events'] });
      // Also invalidate any ticketed event queries with different include params
      queryClient.invalidateQueries({
        queryKey: ['ticketedEvent'],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['event', variables.eventId],
      });
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({
        queryKey: ['event'],
        exact: false,
      });
      onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}
